/* Support centre (sections/support-*, main-support-*; main-contact uses the chat link).
   - <mw-support-search>: search over the JSON index the page carries (snippets/support-search).
   - [data-chat]: opens Shopify Inbox; with Inbox off the link (the contact page) is followed.
   - [data-support-toc]: «På denne side» from the article's own headings, with the one in view marked.
   The contact form itself is assets/contact.js (<mw-contact>). */

(() => {
  if (window.Munkwell?.support) return;
  window.Munkwell = window.Munkwell || {};
  window.Munkwell.support = true;

  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'oe')
      .replace(/å/g, 'aa')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class MwSupportSearch extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.input = this.querySelector('[data-support-input]');
      this.panel = this.querySelector('[data-support-panel]');
      this.list = this.querySelector('[data-support-results]');
      this.empty = this.querySelector('[data-support-empty]');
      this.clear = this.querySelector('[data-support-clear]');
      this.live = this.querySelector('[data-support-live]');
      const raw = this.querySelector('[data-support-index]');
      if (!this.input || !this.list || !raw) return;
      this.wired = true;
      try {
        this.index = JSON.parse(raw.textContent);
      } catch (e) {
        this.index = [];
      }
      this.index.forEach((a) => {
        a._t = norm(a.t);
        a._all = norm([a.t, a.k, a.s, a.c].join(' '));
      });
      this.active = -1;

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 80);
      });
      this.input.addEventListener('keydown', (e) => this.key(e));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim()) this.search();
      });
      this.clear?.addEventListener('click', () => {
        this.input.value = '';
        this.close();
        this.input.focus();
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
    }

    search() {
      const q = norm(this.input.value.trim());
      this.clear && (this.clear.hidden = !q);
      if (q.length < 2) return this.close();
      const words = q.split(/\s+/).filter(Boolean);
      const hits = this.index
        .map((a) => {
          if (!words.every((w) => a._all.includes(w))) return null;
          let score = 0;
          words.forEach((w) => {
            if (a._t.startsWith(w)) score += 6;
            else if (a._t.includes(w)) score += 4;
            else score += 1;
          });
          return { a, score };
        })
        .filter(Boolean)
        .sort((x, y) => y.score - x.score)
        .slice(0, 6);

      this.hits = hits.map((h) => h.a);
      this.active = -1;
      const id = this.list.id;
      this.list.innerHTML = this.hits
        .map(
          (a, i) =>
            `<li role="option" id="${id}-${i}" aria-selected="false"><a class="ssearch__hit" href="${escapeHtml(a.u)}" tabindex="-1">` +
            `<span class="ssearch__hitcat">${escapeHtml(a.c)}</span>` +
            `<span class="ssearch__hittitle">${this.mark(a.t, words)}</span>` +
            (a.s ? `<span class="ssearch__hitsum">${escapeHtml(a.s)}</span>` : '') +
            `</a></li>`
        )
        .join('');
      this.list.hidden = this.hits.length === 0;
      if (this.empty) this.empty.hidden = this.hits.length > 0;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      const found = this.hits.length === 1 ? this.dataset.foundOne : this.dataset.foundOther;
      this.live.textContent = this.hits.length
        ? (found || '%%COUNT%%').replace('%%COUNT%%', String(this.hits.length))
        : this.dataset.foundNone || '';
    }

    // Wrap the typed words in <mark>, matched on the folded text so «ae» finds «æ».
    mark(title, words) {
      const folded = norm(title);
      if (folded.length !== title.length) return escapeHtml(title);
      const hit = new Array(title.length).fill(false);
      words.forEach((w) => {
        let at = folded.indexOf(w);
        while (at > -1) {
          for (let i = at; i < at + w.length; i++) hit[i] = true;
          at = folded.indexOf(w, at + w.length);
        }
      });
      let out = '';
      let open = false;
      [...title].forEach((ch, i) => {
        if (hit[i] && !open) (out += '<mark>'), (open = true);
        if (!hit[i] && open) (out += '</mark>'), (open = false);
        out += escapeHtml(ch);
      });
      return open ? out + '</mark>' : out;
    }

    key(e) {
      const n = this.hits?.length || 0;
      if (e.key === 'Escape') {
        if (!this.panel.hidden) {
          e.preventDefault();
          this.close();
        } else if (this.input.value) {
          this.input.value = '';
          this.clear && (this.clear.hidden = true);
        }
        return;
      }
      if (this.panel.hidden || !n) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
        [...this.list.children].forEach((li, i) => li.setAttribute('aria-selected', String(i === this.active)));
        this.input.setAttribute('aria-activedescendant', `${this.list.id}-${this.active}`);
        this.list.children[this.active]?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = this.hits[Math.max(this.active, 0)];
        if (target) window.location.href = target.u;
      }
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }
  }
  if (!customElements.get('mw-support-search')) customElements.define('mw-support-search', MwSupportSearch);

  /* Chat: Shopify Inbox puts <shopify-chat> in the page a few seconds after load. A click before
     that marks the button busy and waits up to 2.5 s, then goes to the link instead. */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-chat]');
    if (!t) return;
    const widget = () => {
      const el = document.querySelector('shopify-chat');
      return el && typeof el.show === 'function' ? el : null;
    };
    if (!document.querySelector('shopify-chat, #shopify-chat-app-embed-data')) return;
    e.preventDefault();
    const w = widget();
    if (w) return w.show();
    t.setAttribute('aria-busy', 'true');
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      clearInterval(poll);
      t.removeAttribute('aria-busy');
      const ww = widget();
      if (ww) ww.show();
      else if (t.href) window.location.assign(t.href);
    };
    const poll = setInterval(() => widget() && go(), 150);
    setTimeout(go, 2500);
  });

  /* «På denne side»: only worth showing with three or more headings. */
  const toc = () => {
    const nav = document.querySelector('[data-support-toc]');
    const content = document.querySelector('[data-support-content]');
    if (!nav || !content) return;
    const heads = [...content.querySelectorAll('h2, h3')];
    if (heads.length < 3) return;
    const list = nav.querySelector('[data-support-toc-list]');
    const used = new Set();
    const links = heads.map((h, i) => {
      let id = h.id || norm(h.textContent).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `afsnit-${i + 1}`;
      while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== h)) id += '-' + (i + 1);
      used.add(id);
      h.id = id;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'stoc__link';
      a.href = `#${id}`;
      a.textContent = h.textContent.trim();
      li.append(a);
      list.append(li);
      return a;
    });
    nav.hidden = false;
    if (!('IntersectionObserver' in window)) return;
    const seen = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => seen.set(en.target, en.isIntersecting));
        const current = heads.find((h) => seen.get(h)) || null;
        if (!current) return;
        links.forEach((a) => a.toggleAttribute('aria-current', a.hash === `#${current.id}`));
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );
    heads.forEach((h) => io.observe(h));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', toc);
  else toc();
})();
